import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { AuthUserDTO, RestaurantDTO } from '@scan2eat/shared';
import { apiFetch, setAccessToken } from '../api/client.js';

interface AuthState {
  user: AuthUserDTO;
  /**
   * Nul pour un PLATFORM_ADMIN qui n'a bascule sur aucun restaurant (ADR 0009).
   * Le type le disait non-nul, et deux lectures sans precaution faisaient
   * tomber toute l'application des qu'un tel compte se connectait.
   */
  restaurant: RestaurantDTO | null;
}

interface AuthContextValue {
  auth: AuthState | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: AuthUserDTO) => void;
  updateRestaurant: (restaurant: RestaurantDTO) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit etre utilise dans AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ user: AuthUserDTO; restaurant: RestaurantDTO | null }>('/admin/auth/me')
      .then((data) => { setAuth(data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ accessToken: string; user: AuthUserDTO; restaurant: RestaurantDTO | null }>(
      '/admin/auth/login',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) },
    );
    setAccessToken(data.accessToken);
    setAuth({ user: data.user, restaurant: data.restaurant });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/admin/auth/logout', { method: 'POST' });
    } catch { /* noop */ }
    setAccessToken(null);
    setAuth(null);
    window.location.href = '/connexion';
  }, []);


  const updateUser = useCallback((user: AuthUserDTO) => {
    setAuth((prev) => (prev ? { ...prev, user } : prev));
  }, []);

  const updateRestaurant = useCallback((restaurant: RestaurantDTO) => {
    setAuth((prev) => (prev ? { ...prev, restaurant } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{ auth, loading, login, logout, updateUser, updateRestaurant }}
    >
      {children}
    </AuthContext.Provider>
  );
}
